<template>
  <v-row dense>
    <v-col cols="12">
      <!-- Q9 ทำ 5 แท็บให้บันทึกจริงแล้ว แต่ยังไม่มีตาราง QaSettingsHistory —
           ถ้าไม่บอกตรงนี้ คนจะเข้าใจว่ารายการข้างล่างคือประวัติจริงของแท็บพวกนั้น
           ทั้งที่มันเป็นข้อมูลตัวอย่างล้วน · ที่จริงมีแค่ "แก้ล่าสุดโดยใคร" ต่อ section -->
      <v-alert type="warning" variant="tonal" density="compact" icon="mdi-flask-outline">
        <span class="text-body-2">
          {{ $t('qaAudMockNote') }}
        </span>
      </v-alert>
    </v-col>

    <v-col cols="12" md="5">
      <v-card class="qa-card-sm" variant="outlined">
        <v-card-title class="py-3"><span class="text-subtitle-1">{{ $t('qaAudRolesTitle') }}</span></v-card-title>
        <v-divider />
        <v-table density="comfortable">
          <thead>
            <tr>
              <th>{{ $t('qaAudColRole') }}</th>
              <th>{{ $t('qaAudColCan') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="role in roles" :key="role.name">
              <td class="text-body-2 font-weight-medium">{{ role.name }}</td>
              <td class="text-body-2 text-medium-emphasis">{{ role.can }}</td>
            </tr>
          </tbody>
        </v-table>
        <v-divider />
        <div class="pa-4">
          <div class="d-flex align-center ga-3">
            <div class="flex-grow-1">
              <div class="text-body-2 font-weight-medium">{{ $t('qaAudFourEyes') }}</div>
              <div class="text-caption text-medium-emphasis">{{ $t('qaAudFourEyesDesc') }}</div>
            </div>
            <v-switch v-model="fourEyes" color="primary" density="compact" hide-details inset />
          </div>
        </div>
      </v-card>
    </v-col>

    <v-col cols="12" md="7">
      <v-card class="qa-card-sm" variant="outlined">
        <v-card-title class="d-flex align-center py-3">
          <span class="text-subtitle-1">{{ $t('qaAudHistoryTitle') }}</span>
          <QaMockChip :text="$t('qaAudMockChip')" :reason="$t('qaAudMockReason')" class="ml-2" />
          <v-spacer />
          <span class="text-caption text-medium-emphasis">{{ $t('qaAudLatest', { n: entries.length }) }}</span>
        </v-card-title>
        <v-divider />
        <v-table density="comfortable" class="qa-audit-table">
          <thead>
            <tr>
              <th style="width: 150px">{{ $t('qaAudColTime') }}</th>
              <th style="width: 120px">{{ $t('qaAudColUser') }}</th>
              <th>{{ $t('qaAudColChange') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="entry in entries" :key="entry.auditId">
              <td class="text-caption qa-num">{{ formatDateTime(entry.changedAt) }}</td>
              <td class="text-body-2">{{ entry.changedBy }}</td>
              <td class="text-body-2">
                <v-chip size="x-small" variant="tonal" label class="mr-2">{{ entry.entityType }}</v-chip>
                {{ entry.summary }}
              </td>
            </tr>
            <tr v-if="!entries.length">
              <td colspan="3" class="text-center text-medium-emphasis py-6">{{ $t('qaAudEmpty') }}</td>
            </tr>
          </tbody>
        </v-table>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import { formatDateTime } from '@/utils/qaFormat';
import QaMockChip from '@/components/QAConsole/QaMockChip.vue';

/**
 * แถวตัวอย่างของ "ประวัติการเปลี่ยนแปลง"
 *
 * ⚠️ ย้ายมาอยู่ในไฟล์นี้เพราะแหล่งเดิม (ชั้น mock ของ service) ถูกลบทิ้งทั้งชั้น
 *    จงใจให้เป็นค่าคงที่ที่ **อ่านออกตั้งแต่บรรทัดแรกว่าเป็นของปลอม** แทนที่จะให้มัน
 *    เดินทางผ่าน service เหมือนข้อมูลจริง — ของปลอมที่มาทาง API เดียวกับของจริงคือ
 *    ของปลอมที่รอดจากการเก็บกวาดครั้งหน้าได้อีก
 *    เมื่อมีตาราง QaSettingsHistory จริงแล้ว ให้ลบก้อนนี้พร้อมป้าย QaMockChip ทั้งคู่
 */
const SAMPLE_AUDIT_ENTRIES = [
  { auditId: 'aud-4', changedAt: '2026-08-10T07:22:00.000Z', changedBy: 'Suwadee W.', entityType: 'Rubric', summaryKey: 'qaAudMock4' },
  { auditId: 'aud-3', changedAt: '2026-08-10T04:05:00.000Z', changedBy: 'Suwadee W.', entityType: 'Rubric', summaryKey: 'qaAudMock3' },
  { auditId: 'aud-2', changedAt: '2026-08-08T09:40:00.000Z', changedBy: 'Admin', entityType: 'Policy', summaryKey: 'qaAudMock2' },
  { auditId: 'aud-1', changedAt: '2026-08-05T02:12:00.000Z', changedBy: 'Admin', entityType: 'Rubric', summaryKey: 'qaAudMock1' },
];

export default {
  name: 'QaAuditLog',
  components: { QaMockChip },
  data() {
    return {
      fourEyes: true,
    };
  },
  computed: {
    // ⚠️ computed ไม่ใช่ data — this.$t ใน data() ไม่ reactive ต่อ locale
    //    ถ้าอยู่ใน data() ตารางบทบาทกับแถวตัวอย่างจะค้างภาษาแรกที่ mount
    roles() {
      return [
        { name: 'Super Admin', can: this.$t('qaAudRoleSuper') },
        { name: 'QA Manager', can: this.$t('qaAudRoleManager') },
        { name: 'QA Supervisor', can: this.$t('qaAudRoleSupervisor') },
        { name: 'Team Lead', can: this.$t('qaAudRoleLead') },
      ];
    },
    /** แถวตัวอย่าง — ข้อความสรุปอยู่ใน i18n เพื่อให้สลับภาษาแล้วเปลี่ยนตาม */
    entries() {
      return SAMPLE_AUDIT_ENTRIES.map((e) => ({ ...e, summary: this.$t(e.summaryKey) }));
    },
  },
  methods: { formatDateTime },
};
</script>

<style scoped>
.qa-audit-table :deep(th),
:deep(th) {
  background: #f5f5f5;
  font-size: 11px;
  text-transform: uppercase;
  color: #6b7280;
  font-weight: 600;
}
</style>
